import { UseGQLQuery } from 'hooks/useGQLQuery/useGQLQuery.types';
import { useSendToAgenciesFormContext } from 'modals/ShiftModal/src/views/SendToAgenciesView/SendToAgencies.form.context';
import { FC, useCallback, useContext, useMemo } from 'react';
import { createGQLDataContext } from 'utils/createDataContext';
import {
  IFetchAgenciesByAgencyIdsData,
  useFetchAgenciesByAgencyIdsDataContext,
} from 'modals/ShiftModal/src/views/SendToAgenciesView/SendToAgenciesSummaryView/AgencyRequests/FetchAgenciesByAgencyIds.data.context';
import {
  IFetchMarketplaceByCommunityIdsData,
  useFetchMarketplaceByCommunityIdsDataContext,
} from 'modals/ShiftModal/src/views/SendToAgenciesView/SendToAgenciesSummaryView/AgencyRequests/FetchMarketplaceByCommunityIds.data.context';

interface ICombinedSummaryInfoData {
  selectedAgencies: string[];
  availableJaneCount: number;
  hourlyRate: {
    min: number;
    max: number;
  };
  agencyInfo?: IFetchAgenciesByAgencyIdsData;
  marketplaceInfo?: IFetchMarketplaceByCommunityIdsData;
  pageInfo: {
    hasNextPage: boolean;
  };
}

export const FetchCombinedSummaryInfoDataContext = createGQLDataContext<ICombinedSummaryInfoData>(
  {} as ICombinedSummaryInfoData,
);

export const FetchCombinedSummaryInfoDataProvider: FC = ({ children }) => {
  const agencyInfo = useFetchAgenciesByAgencyIdsDataContext();
  const marketplaceInfo = useFetchMarketplaceByCommunityIdsDataContext();
  const {
    values: { selectedAgencies, selectedMarketplaceCommunities },
  } = useSendToAgenciesFormContext();

  const combinedData = useMemo(() => {
    const agencyNodes = agencyInfo.data?.selectedAgencies || [];
    const marketplaceNodes = marketplaceInfo.data?.selectedMarketplaceCommunities || [];

    const agencyHourlyRate = agencyInfo.data?.hourlyRate;
    const marketplaceHourlyRate = marketplaceInfo.data?.hourlyRate;

    const minRates = [];
    const maxRates = [];

    if (agencyHourlyRate?.min !== undefined) minRates.push(agencyHourlyRate.min);
    if (agencyHourlyRate?.max !== undefined) maxRates.push(agencyHourlyRate.max);
    if (marketplaceHourlyRate?.min !== undefined) minRates.push(marketplaceHourlyRate.min);
    if (marketplaceHourlyRate?.max !== undefined) maxRates.push(marketplaceHourlyRate.max);

    return {
      selectedAgencies: [...agencyNodes, ...marketplaceNodes],
      availableJaneCount:
        (agencyInfo.data?.availableJaneCount || 0) +
        (marketplaceInfo.data?.availableJaneCount || 0),
      hourlyRate: {
        min: minRates.length > 0 ? Math.min(...minRates) : 0,
        max: maxRates.length > 0 ? Math.max(...maxRates) : 0,
      },
      agencyInfo: agencyInfo.data,
      marketplaceInfo: marketplaceInfo.data,
      pageInfo: {
        hasNextPage: false,
      },
    };
  }, [agencyInfo.data, marketplaceInfo.data]);

  const onRefetch = useCallback(() => {
    agencyInfo.fetch();
    marketplaceInfo.fetch();
  }, [agencyInfo, marketplaceInfo]);

  const onChange = useCallback((e) => {
    // No-op
  }, []);

  const loadMore = useCallback(() => {
    // No-op
  }, []);

  const status = useMemo(() => {
    const isAgencyActive = selectedAgencies.length > 0;
    const isMarketplaceActive = selectedMarketplaceCommunities.length > 0;

    if (
      (isAgencyActive && agencyInfo.status === 'ERROR') ||
      (isMarketplaceActive && marketplaceInfo.status === 'ERROR')
    ) {
      return 'ERROR';
    }

    if (
      (isAgencyActive && agencyInfo.status === 'LOADING') ||
      (isMarketplaceActive && marketplaceInfo.status === 'LOADING')
    ) {
      return 'LOADING';
    }

    return 'READY';
  }, [agencyInfo.status, marketplaceInfo.status, selectedAgencies, selectedMarketplaceCommunities]);

  const contextValue = useMemo(
    () => ({
      ...agencyInfo,
      data: combinedData,
      status,
      onRefetch,
      onChange,
      loadMore,
      fetch: onRefetch,
      __typename: 'GraphQLAPI' as const,
    }),
    [agencyInfo, combinedData, status, onRefetch, onChange, loadMore],
  );

  return (
    <FetchCombinedSummaryInfoDataContext.Provider value={contextValue}>
      {children}
    </FetchCombinedSummaryInfoDataContext.Provider>
  );
};

export const useFetchCombinedSummaryInfoDataContext = (): UseGQLQuery<ICombinedSummaryInfoData> =>
  useContext(FetchCombinedSummaryInfoDataContext);
